import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useAppContext } from "../../api/AppContext";
import { useSnackbar } from "notistack";
import ImageUpload from "../ImageUpload";
import OutlinedButton from "../OutlinedButton";

const ImageInsertModal = ({
  open,
  onClose,
  gallery = null,
  title = "Add image to gallery",
  onSuccess,
}) => {
  const { listImageGalleries, updateTeamsGalleryImage, updateImageGallery } =
    useAppContext();
  const { enqueueSnackbar } = useSnackbar();

  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingGalleries, setIsLoadingGalleries] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(gallery || null);

  // removed extra fields: only image upload remains

  // Normalize gallery data
  const normalizeGallery = useCallback((g) => {
    const companies = Array.isArray(g?.companies) ? g.companies : [];
    const companyId = companies.length > 0 ? companies[0]?.id : "";
    const access = companies.map((c) => c?.accessType).filter(Boolean);
    return {
      id: g?.id,
      name: g?.name,
      companyId,
      createdAt: g?.created_at ?? g?.createdAt ?? null,
      accessType: access,
      companies,
    };
  }, []);

  // If a gallery is provided via props, normalize and set it
  useEffect(() => {
    if (gallery) {
      setSelectedGallery(normalizeGallery(gallery));
    }
  }, [gallery, normalizeGallery]);

  // Preload galleries if not provided
  useEffect(() => {
    let mounted = true;
    if (!open || gallery) return;

    const load = async () => {
      setIsLoadingGalleries(true);
      try {
        const list = await listImageGalleries();
        if (!mounted) return;
        const source = Array.isArray(list) ? list : [];
        setGalleries(source.map(normalizeGallery));
      } catch (e) {
        console.error("Failed to load galleries", e);
        enqueueSnackbar(e?.message || "Failed to load galleries", {
          variant: "error",
        });
      } finally {
        if (mounted) setIsLoadingGalleries(false);
      }
    };
    load();

    return () => {
      mounted = false;
    };
  }, [open, gallery, listImageGalleries, normalizeGallery, enqueueSnackbar]);

  // Compute cid from selected gallery across possible shapes
  const computedCid =
    selectedGallery?.companyId ||
    (Array.isArray(selectedGallery?.companies)
      ? selectedGallery.companies[0]?.id
      : undefined);

  // handle upload of single or multiple images
  const handleInsertImage = useCallback(
    async (payload) => {
      if (!selectedGallery?.id || !payload) return;
      try {
        setIsUploading(true);

        const items = Array.isArray(payload) ? payload : [payload];

        // Create images first
        const created = [];

        for (const it of items) {
          const { url, name } = it || {};
          if (!url) continue;
          const payload = { isNew: true, url };
          if (name) payload.imagename = name; // do not derive from URL; keep empty if not provided
          const img = await updateTeamsGalleryImage(payload);
          created.push(img);
        }

        if (created.length > 0) {
          await updateImageGallery({
            galleryId: selectedGallery.id,
            addImageIds: created.map((x) => x.id),
          });
        }

        enqueueSnackbar(
          created.length > 1
            ? `${created.length} images uploaded successfully`
            : "Image uploaded successfully",
          { variant: "success" }
        );
        onSuccess?.(created, selectedGallery);
      } catch (e) {
        console.error("Error uploading image(s):", e);
        enqueueSnackbar(e?.message || "Failed to upload image(s)", {
          variant: "error",
        });
      } finally {
        setIsUploading(false);
      }
    },
    [
      selectedGallery,
      updateTeamsGalleryImage,
      updateImageGallery,
      enqueueSnackbar,
      onSuccess,
    ]
  );

  return (
    <Dialog fullWidth maxWidth="md" open={!!open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {/* Select Gallery */}
        {gallery ? (
          <Typography color="textSecondary" variant="body2">
            Add a new image to: <strong>{gallery?.name}</strong>
          </Typography>
        ) : (
          <Box mb={2}>
            <TextField
              fullWidth
              select
              disabled={isLoadingGalleries}
              helperText={isLoadingGalleries ? "Loading galleries..." : ""}
              label="Select gallery"
              value={selectedGallery?.id || ""}
              variant="outlined"
              onChange={(e) => {
                const g = galleries.find(
                  (x) => String(x.id) === String(e.target.value)
                );
                setSelectedGallery(g || null);
              }}
            >
              {isLoadingGalleries ? (
                <MenuItem value="">
                  <Box alignItems="center" display="flex" gap={1}>
                    <CircularProgress size={18} /> Loading...
                  </Box>
                </MenuItem>
              ) : (
                [
                  <MenuItem key="none" value="">
                    Select a gallery
                  </MenuItem>,
                  ...galleries.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  )),
                ]
              )}
            </TextField>
          </Box>
        )}

        {/* Upload Section */}
        <ImageUpload
          multiple
          customPathInfo={{
            // Ensure galleryId is always sent when a gallery prop exists to force stc/galleries path
            galleryId: gallery?.id || selectedGallery?.id || undefined,
            // Keep cid for backward compatibility; backend ignores it when galleryId is present
            cid: computedCid,
          }}
          // Prevent uploads while no gallery is resolved to avoid falling back to cid/stc
          disabled={isUploading || !(gallery?.id || selectedGallery?.id)}
          showRemoveButton={false}
          onChange={handleInsertImage}
        />
      </DialogContent>
      <DialogActions>
        <OutlinedButton disabled={isUploading} onClick={onClose}>
          Cancel
        </OutlinedButton>
      </DialogActions>
    </Dialog>
  );
};

export default ImageInsertModal;
